/**
 * Provider Product Price Service
 * Gerencia preços de produtos por fornecedor
 */
import authService from './authService';

export interface ProviderProductPrice {
  product_id: number;
  provider_id: number;
  price: number;
  supplier_code?: string;  // Código do fornecedor para este produto
  provider_name?: string;
  product_sku?: string;
  product_name?: string;
  product_description?: string;
  stock_quantity?: number;
  stock_quantity_available?: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateProviderProductPriceRequest {
  price: number;
  supplier_code?: string;
}

export interface UpdateProviderProductPriceRequest {
  price: number;
  supplier_code?: string;
}

export interface BatchProviderProductPriceRequest {
  provider_prices: Array<{
    provider_id: number;
    price: number;
    supplier_code?: string;
  }>;
}

export interface ProviderProductPricesResponse {
  product_prices: ProviderProductPrice[];
  pagination?: {
    current_page: number;
    page_size: number;
    total_records: number;
    total_pages: number;
  };
  count?: number;
}

export interface ProductWithProvidersResponse {
  id: number;
  sku: string;
  name: string;
  description: string;
  cost: number;
  price: number;
  category_id: number;
  category_name: string;
  stock_quantity: number;
  min_stock: number;
  max_stock: number;
  unit: string;
  barcode: string;
  ncm: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  provider_prices?: ProviderProductPrice[];
  calculated_cost?: number;
}

class ProviderProductPriceService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async handleApiResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData.message ||
        errorData.details?.message ||
        errorData.error ||
        (typeof errorData.details === 'string' ? errorData.details : null) ||
        `Erro ${response.status}: ${response.statusText}`;
      throw new Error(message);
    }
    return response.json();
  }

  /**
   * Criar/Atualizar preço de produto por fornecedor
   */
  async createProviderProductPrice(
    productId: number,
    providerId: number,
    price: number,
    supplierCode?: string
  ): Promise<ProviderProductPrice> {
    const api = await this.getApiClient();
    const response = await api.post(
      `/products/${productId}/providers/${providerId}/price`,
      { price, supplier_code: supplierCode }
    );
    return this.handleApiResponse<ProviderProductPrice>(response);
  }

  /**
   * Obter preço específico de produto por fornecedor
   */
  async getProviderProductPrice(
    productId: number,
    providerId: number
  ): Promise<ProviderProductPrice> {
    const api = await this.getApiClient();
    const response = await api.get(`/products/${productId}/providers/${providerId}/price`);
    return this.handleApiResponse<ProviderProductPrice>(response);
  }

  /**
   * Atualizar preço de produto por fornecedor
   */
  async updateProviderProductPrice(
    productId: number,
    providerId: number,
    price: number,
    supplierCode?: string
  ): Promise<ProviderProductPrice> {
    const api = await this.getApiClient();
    const response = await api.put(
      `/products/${productId}/providers/${providerId}/price`,
      { price, supplier_code: supplierCode }
    );
    return this.handleApiResponse<ProviderProductPrice>(response);
  }

  /**
   * Excluir preço de produto por fornecedor
   */
  async deleteProviderProductPrice(
    productId: number,
    providerId: number
  ): Promise<{ success: boolean; message: string }> {
    const api = await this.getApiClient();
    const response = await api.delete(`/products/${productId}/providers/${providerId}/price`);
    return this.handleApiResponse<{ success: boolean; message: string }>(response);
  }

  /**
   * Listar todos os preços de um produto
   */
  async getProductProviderPrices(productId: number): Promise<ProviderProductPricesResponse> {
    const api = await this.getApiClient();
    const response = await api.get(`/products/${productId}/provider-prices`);
    return this.handleApiResponse<ProviderProductPricesResponse>(response);
  }

  /**
   * Listar produtos com preços por fornecedor (com paginação)
   */
  async getProviderProductPrices(
    providerId: number,
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc'
  ): Promise<ProviderProductPricesResponse> {
    const api = await this.getApiClient();
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const sortParam = sortBy ? `&sort_by=${sortBy}&sort_direction=${sortDirection || 'asc'}` : '';
    const response = await api.get(
      `/providers/${providerId}/products?page=${page}&page_size=${pageSize}${searchParam}${sortParam}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
        errorData.details?.message ||
        `HTTP error! status: ${response.status}`
      );
    }

    const json = await response.json();
    console.debug('[ProviderProductPriceService] getProviderProductPrices raw response:', JSON.stringify(json, null, 2));

    // Normaliza múltiplos formatos de envelope:
    // { success, data: { data: [...], pagination: {...} } }
    // { success, data: [...] }
    // { product_prices: [...], pagination: {...} }
    // { data: [...] }
    let rawItems: any[] = [];
    let paginationRaw: any = {};

    if (Array.isArray(json?.data?.data)) {
      rawItems = json.data.data;
      paginationRaw = json.data.pagination ?? {};
    } else if (Array.isArray(json?.data)) {
      rawItems = json.data;
      paginationRaw = json.pagination ?? {};
    } else if (Array.isArray(json?.product_prices)) {
      rawItems = json.product_prices;
      paginationRaw = json.pagination ?? {};
    } else if (Array.isArray(json)) {
      rawItems = json;
    }

    if (rawItems.length > 0) {
      console.debug('[ProviderProductPriceService] first raw item keys:', Object.keys(rawItems[0]));
      console.debug('[ProviderProductPriceService] first raw item values:', rawItems[0]);
    } else {
      console.debug('[ProviderProductPriceService] no items returned from API');
    }

    // Normaliza campos cobrindo as variações de nome de campo possíveis na API
    const items: ProviderProductPrice[] = rawItems.map((item: any) => {
      const normalized: ProviderProductPrice & { product_name?: string } = {
        ...item,
        // ID: product_id ou id
        product_id: item.product_id ?? item.id,
        provider_id: item.provider_id ?? providerId,
        // Preço: provider_price tem prioridade sobre o preço de venda do produto (price)
        price: parseFloat(
          item.provider_price ?? item.unit_price ?? item.cost ?? item.price ?? 0
        ),
        // Código do fornecedor
        supplier_code: item.supplier_code ?? undefined,
        // SKU: product_sku ou sku
        product_sku: item.product_sku ?? item.sku ?? undefined,
        // Nome (campo extra, usado pelo componente)
        product_name: item.product_name ?? item.name ?? item.product_sku ?? item.sku ?? undefined,
        // Descrição do produto
        product_description: item.product_description ?? item.description ?? undefined,
        // Estoque
        stock_quantity: item.stock_quantity ?? undefined,
        stock_quantity_available: item.stock_quantity_available ?? undefined,
        created_at: item.created_at ?? new Date().toISOString(),
      };
      return normalized;
    });

    console.debug('[ProviderProductPriceService] normalized items:', items);

    return {
      product_prices: items,
      pagination: {
        current_page: paginationRaw.page ?? paginationRaw.current_page ?? page,
        page_size: paginationRaw.page_size ?? paginationRaw.limit ?? pageSize,
        total_records: paginationRaw.total_items ?? paginationRaw.total ?? items.length,
        total_pages: paginationRaw.total_pages ?? paginationRaw.pages ?? 1,
      }
    };
  }

  /**
   * Obter produto com todos os fornecedores
   */
  async getProductWithProviders(productId: number): Promise<ProductWithProvidersResponse> {
    const api = await this.getApiClient();
    const response = await api.get(`/products/${productId}/with-providers`);
    return this.handleApiResponse<ProductWithProvidersResponse>(response);
  }

  /**
   * Criação em lote de preços
   */
  async batchCreateProviderProductPrices(
    productId: number,
    providerPrices: Array<{ provider_id: number; price: number; supplier_code?: string }>
  ): Promise<ProviderProductPricesResponse> {
    const api = await this.getApiClient();
    const response = await api.post(
      `/products/${productId}/provider-prices/batch`,
      { provider_prices: providerPrices }
    );
    return this.handleApiResponse<ProviderProductPricesResponse>(response);
  }

  /**
   * Recalcular custo do produto
   */
  async recalculateProductCost(productId: number): Promise<{
    product: ProductWithProvidersResponse;
    message: string
  }> {
    const api = await this.getApiClient();
    const response = await api.post(`/products/${productId}/recalculate-cost`);
    return this.handleApiResponse<{
      product: ProductWithProvidersResponse;
      message: string
    }>(response);
  }

  /**
   * Recalcular custos de todos os produtos
   */
  async recalculateAllProductsCost(): Promise<{
    success: boolean;
    message: string
  }> {
    const api = await this.getApiClient();
    const response = await api.post('/products/recalculate-all-costs');
    return this.handleApiResponse<{
      success: boolean;
      message: string
    }>(response);
  }
}

// Instância singleton do serviço
export const providerProductPriceService = new ProviderProductPriceService();